/**
 * Material Configuration for Different Visual Modes
 * Supports independent stacking of ghost mode and color mode
 */

import useWorkflowStore from '../stores/useWorkflowStore';
import { isAlignmentLockingSocketStep } from './flowConfig';

/** Neutral socket tint when locking/valve/logo steps have no vertex colors (CSG/checkpoint STL). */
export const SOCKET_NEUTRAL_COLOR = 0x565654;

// Base properties for each mode component
export const MODE_PROPERTIES = {
  // Ghost mode properties - affects transparency and polygon offset
  GHOST: {
    opacity: 0.5,
    transparent: true,
    polygonOffsetFactor: 2,
    description: 'Semi-transparent - see internal geometry'
  },

  // Default (non-ghost) properties
  DEFAULT_OPACITY: {
    opacity: 1.0,
    transparent: false,
    polygonOffsetFactor: -2,
    description: 'Opaque default'
  },

  // Color mode properties - affects color
  COLOR: {
    color: 0xff8e00, // orange - for contrast with blue preview materials
    vertexColors: false,
    description: 'Orange color - highlight differences from blue tools'
  },

  // Default (no color tint) properties
  NO_COLOR_TINT: {
    color: 0xffffff, // white - shows vertex colors as-is
    vertexColors: true,
    description: 'Default white - shows vertex colors'
  }
};

// Common polygon offset settings
export const POLYGON_OFFSET_BASE = {
  polygonOffset: true,
  polygonOffsetUnits: 1.0
};

/**
 * Compute material configuration based on independent ghost and color mode flags
 * @param {boolean} ghostModeEnabled - Whether ghost mode is active
 * @param {boolean} colorModeEnabled - Whether color mode is active
 * @param {boolean} [hasVertexColors=true] - geometry has a `color` attribute
 * @returns {Object} Composed material configuration
 */
export const computeMaterialConfig = (ghostModeEnabled, colorModeEnabled, hasVertexColors = true) => {
  // Start with polygon offset base
  const config = { ...POLYGON_OFFSET_BASE };

  // Apply opacity and transparency based on ghost mode
  const opacityProps = ghostModeEnabled ? MODE_PROPERTIES.GHOST : MODE_PROPERTIES.DEFAULT_OPACITY;
  config.opacity = opacityProps.opacity;
  config.transparent = opacityProps.transparent;
  config.polygonOffsetFactor = opacityProps.polygonOffsetFactor;

  // Apply color based on color mode (vertexColors only when geometry actually has colors)
  const step = useWorkflowStore.getState().currentStep;
  const useSocketNeutral =
    !colorModeEnabled &&
    !hasVertexColors &&
    isAlignmentLockingSocketStep(step);

  if (useSocketNeutral) {
    config.color = SOCKET_NEUTRAL_COLOR;
    config.vertexColors = false;
  } else {
    const colorProps = colorModeEnabled ? MODE_PROPERTIES.COLOR : MODE_PROPERTIES.NO_COLOR_TINT;
    config.color = colorProps.color;
    config.vertexColors = hasVertexColors && colorProps.vertexColors;
  }

  return config;
};

/**
 * Apply computed material configuration to a material object
 * @param {THREE.Material} material - The Three.js material to update
 * @param {boolean} ghostModeEnabled - Whether ghost mode is active
 * @param {boolean} colorModeEnabled - Whether color mode is active
 * @param {THREE.BufferGeometry} [geometry] - active shaded mesh geometry (for vertex color detection)
 */
export const applyMaterialModes = (material, ghostModeEnabled, colorModeEnabled, geometry = null) => {
  if (!material) {
    console.warn(`Material is null or undefined`);
    return;
  }

  const hasVertexColors = !!geometry?.attributes?.color;
  const config = computeMaterialConfig(ghostModeEnabled, colorModeEnabled, hasVertexColors);

  material.opacity = config.opacity;
  material.transparent = config.transparent;
  material.vertexColors = config.vertexColors;
  material.color.set(config.color);
  material.polygonOffset = config.polygonOffset;
  material.polygonOffsetFactor = config.polygonOffsetFactor;
  material.polygonOffsetUnits = config.polygonOffsetUnits;
  material.needsUpdate = true;
};

/**
 * Visual style for the compare overlay (original mesh snapshot): same opacity / polygon
 * offset semantics as ghost mode so it reads as a reference behind the active model.
 * @param {THREE.Material} material
 * @param {boolean} compareActive
 */
export const styleCompareOverlayMaterial = (material, compareActive) => {
  if (!material) return;
  const g = MODE_PROPERTIES.GHOST;
  if (compareActive) {
    material.opacity = g.opacity;
    material.transparent = g.transparent;
    material.depthWrite = false;
    material.depthTest = false;
    material.polygonOffset = POLYGON_OFFSET_BASE.polygonOffset;
    material.polygonOffsetFactor = g.polygonOffsetFactor;
    material.polygonOffsetUnits = POLYGON_OFFSET_BASE.polygonOffsetUnits;
  } else {
    material.opacity = 0.5;
    material.transparent = true;
    material.depthWrite = false;
    material.depthTest = false;
    material.polygonOffset = POLYGON_OFFSET_BASE.polygonOffset;
    material.polygonOffsetFactor = -0.1;
    material.polygonOffsetUnits = POLYGON_OFFSET_BASE.polygonOffsetUnits;
  }
  material.needsUpdate = true;
};
